import classNames from "classnames";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LuAlertCircle, LuCalendar, LuChevronDown } from "react-icons/lu";
import moment from "moment";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import CustomCurrencyInput from "../../../components/CustomCurrencyInput";
import { SettingsConfirmModal } from "../../../components/modal/SettingsConfirmModal";
import { StyledDatePicker } from "../../../components/datepicker/datepicker";
import { LottieLoading } from "../../../components/graphics/LottieLoading";
import { getInitialsFromFullName } from "../../../constants/constants";
import { useAppSelector } from "../../../redux/redux";
import {
  IAddPayment,
  useAddPaymentMutation,
  useGetPaymentByIdQuery,
  useGetPaymentMethodsQuery,
  useLazyGetInvoicesByCustomerQuery,
  useUpdatePaymentMutation,
} from "../../../services/BillingService";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { useGetSearchCustomersQuery } from "../../../slices/ContactsSlice";
import { SelectOptionType } from "../../../types/types";
import { escapeHtml } from "../../../utils/escapeHtml";
import { serverMessage } from "../../../utils/serverMessage";
import { transformDateToUTC } from "../../../utils/transformDate";
import {
  BILLING_DATE_PICKER_FORMAT,
  formatBillingDate,
  newPaymentDateError,
  paymentDateBounds,
} from "../billingDates.utils";
import {
  AiPaymentState,
  withProposedAmounts,
} from "../../ai/ask/actionProposal.utils";
import { rankClientOptions } from "../ui/clientSearch.utils";
import {
  lineAmount,
  overpaymentWarning,
  paymentAmountProblem,
} from "./paymentAmounts.utils";
import {
  PaymentFormRow,
  paymentFormRows,
  updatePaymentBody,
} from "./paymentEdit.utils";
import {
  BILLING_CARD,
  BILLING_PRIMARY_BUTTON,
  BILLING_SECONDARY_BUTTON,
  BillingCheckbox,
  ClientAvatar,
  formatMoney,
} from "../ui/BillingUi";

type Props = {
  /** Edit this saved payment (PAY-018): the form opens pre-filled. */
  editPaymentId?: string;
};

const AddPayment: React.FC<Props> = ({ editPaymentId }) => {
  const isEdit = !!editPaymentId;
  const [searchParams] = useSearchParams();
  // Ask ProCharted "record a payment …": client, date, method, notes and the
  // amount per invoice start filled in; nothing is saved until Save.
  const location = useLocation();
  const [aiPayment] = useState(() =>
    isEdit
      ? null
      : (location.state as AiPaymentState | null)?.aiPayment ?? null,
  );
  const defaultCustomerId = isEdit ? "" : searchParams?.get("customerId") || "";

  const [customerId, setCustomerId] = useState(defaultCustomerId);

  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.appReducer);

  const [addPayment, { isSuccess: isSuccessAdd, error: addError }] =
    useAddPaymentMutation();
  const [
    updatePayment,
    { isSuccess: isSuccessUpdate, isLoading: isSaving, error: updateError },
  ] = useUpdatePaymentMutation();
  const error = isEdit ? updateError : addError;

  const {
    data: payment,
    isLoading: isLoadingPayment,
    isError: isPaymentError,
  } = useGetPaymentByIdQuery(editPaymentId || "", { skip: !isEdit });
  // The saved payment is put into the form once (later refetches don't
  // overwrite what the person typed).
  const [prefilled, setPrefilled] = useState(false);

  const { data: paymentMethods, isLoading: isLoadingMethods } =
    useGetPaymentMethodsQuery(getLinkedFirm()?.orgId);

  const [defaultSearch, setDefaulSearch] = useState(() =>
    customerId ? "" : "ab",
  );
  const [search, setSearch] = useState("");

  const {
    data: contacts,
    isLoading: isLoadingContacts,
    isFetching: isFetchingContacts,
  } = useGetSearchCustomersQuery({
    orgId: getLinkedFirm()?.orgId || "",
    userId: user.id,
    searchQuery: search || defaultSearch,
    isActive: true,
  });

  const [currentContact, setCurrentContact] = useState<SelectOptionType | null>(
    aiPayment?.customer ?? null,
  );

  const [getInvoices, { data: invoices, isLoading }] =
    useLazyGetInvoicesByCustomerQuery();

  const [data, setData] = useState<PaymentFormRow[]>([]);

  const [paymentDate, setPaymentDate] = useState(() => {
    const day = aiPayment?.paymentDate
      ? moment(aiPayment.paymentDate, "YYYY-MM-DD", true)
      : null;
    return day?.isValid() ? day.toDate() : new Date();
  });
  const [showDateError, setShowDateError] = useState(false);
  const [method, setMethod] = useState("");
  const [notes, setNotes] = useState(aiPayment?.notes ?? "");
  const [amountProblem, setAmountProblem] = useState<string | null>(null);
  const [overpayQuestion, setOverpayQuestion] = useState<string | null>(null);

  const contactsOptions = (): SelectOptionType[] => {
    const options: SelectOptionType[] = [];
    if (contacts) {
      contacts.forEach((contact) => {
        options.push({
          value: contact?.customerId,
          label: `${contact.customerName}`,
          avatar: getInitialsFromFullName(contact?.customerName),
          avatarColor: contact.businessContactId ? "purple" : "blue",
        });
      });
    }

    // PAY-009: clients whose NAME matches the search first.
    return rankClientOptions(options, search);
  };

  // Same date rule as "Record a payment" (QA C2): not in the future, not
  // before the date of an invoice that gets an amount.
  const dateError = newPaymentDateError(
    paymentDate,
    data
      .filter((item) => (+item.amountPaid || 0) > 0)
      .map((item) => item.invoiceDate),
  );

  const paymentLines = data.map((item) => ({
    amountPaid: item.amountPaid,
    balance: item.balance,
    invoiceNo: item.invoiceId,
    previousAmount: item.previousAmount,
  }));

  const createPayment = (allowOverpayment = false) => {
    if (dateError) {
      setShowDateError(true);
      return;
    }
    // PAY-013 / PAY-014 (QA batch 17): more than $0, nothing negative, and ask
    // before paying more than a balance.
    const problem = paymentAmountProblem(paymentLines);
    setAmountProblem(problem);
    if (problem) return;
    if (!allowOverpayment) {
      const question = overpaymentWarning(paymentLines);
      if (question) {
        setOverpayQuestion(question);
        return;
      }
    }
    if (isEdit && payment && currentContact?.value && paymentDate) {
      updatePayment(
        updatePaymentBody({
          paymentId: payment.id,
          customerId: String(currentContact.value),
          paymentDate,
          paymentMethodId: method,
          notes,
          paymentStatusId: payment.paymentStatusId || null,
          rows: data,
          allowOverpayment,
        }),
      );
      return;
    }
    if (currentContact?.value && paymentDate) {
      let amountPaid = 0;
      let balance = 0;
      data.forEach((item) => {
        amountPaid += lineAmount(item.amountPaid);
        balance += item.balance;
      });
      const body: IAddPayment = {
        paymentDate: paymentDate.toISOString(),
        customerId: currentContact?.value,
        amount: amountPaid,
        balance: balance,
        notes: notes,
        orgId: getLinkedFirm()?.orgId,
        paymentMethodId: method,
        userId: user?.id,
        allowOverpayment,
        paymentItem:
          data
            ?.filter((item) => lineAmount(item.amountPaid) > 0)
            .map((item) => ({
              invoiceId: item.id,
              amountPaid: lineAmount(item.amountPaid),
            })) || [],
      };
      addPayment(body);
    }
  };

  useEffect(() => {
    if (currentContact) {
      getInvoices(currentContact.value || "");
    }
  }, [currentContact?.customerId, currentContact]);

  useEffect(() => {
    if (!invoices) return;
    // Edit: the saved client's rows carry this payment's amounts; another
    // client starts empty (saving then moves the payment, PAY-018).
    const savedLines =
      isEdit && payment && currentContact?.value === payment.customerId
        ? payment.paymentItem
        : null;
    const rows = paymentFormRows(invoices, savedLines);
    // The proposed amounts apply to the proposed client only.
    setData(
      aiPayment && currentContact?.value === aiPayment.customer.value
        ? withProposedAmounts(rows, aiPayment.amounts)
        : rows,
    );
  }, [invoices, currentContact, isEdit, payment, aiPayment]);

  useEffect(() => {
    if (isEdit) return;
    if (paymentMethods && paymentMethods?.length > 0) {
      const proposed = paymentMethods.find(
        (item) => item.id === aiPayment?.paymentMethodId,
      );
      setMethod((proposed || paymentMethods[0]).id);
    }
  }, [paymentMethods, isEdit, aiPayment]);

  // Edit: client, date, method and notes from the saved payment.
  useEffect(() => {
    if (!isEdit || !payment || prefilled) return;
    const customer = payment.customer;
    const name = customer?.name || payment.customerName || "";
    setCurrentContact({
      value: payment.customerId,
      label: name,
      avatar: getInitialsFromFullName(name),
      avatarColor:
        customer?.businessContactId || customer?.businessContact
          ? "purple"
          : "blue",
    });
    const saved = new Date(payment.paymentDate);
    if (!Number.isNaN(saved.getTime())) setPaymentDate(saved);
    setMethod(payment.paymentMethodId || paymentMethods?.[0]?.id || "");
    setNotes(payment.notes || "");
    setPrefilled(true);
  }, [isEdit, payment, prefilled, paymentMethods]);

  useEffect(() => {
    if (isSuccessUpdate && editPaymentId) {
      navigate("/billing/payments/" + editPaymentId);
    }
  }, [isSuccessUpdate]);

  useEffect(() => {
    if (customerId && contacts?.length && !isLoadingContacts) {
      const options = contactsOptions();

      const defaultCustomer =
        options?.find((item) => {
          return String(item.value).trim() === String(customerId).trim();
        }) || null;

      setCurrentContact(defaultCustomer);
      setCustomerId("");
    }
  }, [customerId, contacts, isLoadingContacts]);

  useEffect(() => {
    setDefaulSearch(customerId ? "" : "ab");
  }, [customerId]);

  useEffect(() => {
    if (isSuccessAdd) {
      navigate("/billing/payments");
    }
  }, [isSuccessAdd]);

  const total = useMemo(() => {
    let value = 0;
    data.forEach((item) => (value += lineAmount(item?.amountPaid)));
    return value;
  }, [data]);

  if (isLoadingMethods || isLoadingContacts || isLoadingPayment) {
    return <LottieLoading />;
  }

  if (isEdit && (isPaymentError || !payment)) {
    return (
      <div className={classNames(BILLING_CARD, "px-[30px] py-7")}>
        <div className={"text-[14px] text-[#3A4256]"}>
          This payment doesn't exist or was deleted.
        </div>
      </div>
    );
  }

  const updateRow = (index: number, changes: Partial<PaymentFormRow>) =>
    setData((prev) =>
      prev.map((invoice, i) =>
        i === index ? { ...invoice, ...changes } : invoice,
      ),
    );

  // The server answers refusals as plain text (QA batch 17).
  const errorMessage =
    amountProblem ||
    (error
      ? (error as { data?: { message?: string } })?.data?.message ||
        serverMessage(
          error,
          "Something went wrong. Please check the amounts and try again.",
        )
      : "");

  return (
    <div className={classNames(BILLING_CARD, "px-[30px] pb-[26px] pt-7")}>
      <h2
        className={
          "m-0 mb-6 text-[22px] font-extrabold tracking-[-0.5px] text-[#1F2433]"
        }
      >
        {isEdit ? "Edit payment" : "Record payment"}
      </h2>

      <div className={"mb-2 text-[12.5px] font-bold text-[#3A4256]"}>
        Client
      </div>
      <div className={"mb-[22px]"}>
        <PaymentClientSelect
          value={currentContact}
          options={contactsOptions()}
          search={search}
          isLoading={isFetchingContacts}
          locked={isEdit}
          onSearch={setSearch}
          onChange={(option) => setCurrentContact({ ...option })}
        />
      </div>

      <div className={"mb-2 flex flex-wrap gap-[26px]"}>
        <div className={"flex flex-col gap-[7px]"}>
          <span className={"text-xs font-bold text-[#9097A8]"}>
            Payment date
          </span>
          <div
            className={
              "flex h-[42px] w-[170px] items-center gap-2.5 overflow-hidden rounded-[9px] border border-[#DFE2E8] bg-white px-3"
            }
          >
            <div className={"min-w-0 flex-1"}>
              <StyledDatePicker
                value={paymentDate}
                maxDate={paymentDateBounds(null).maxDate}
                format={BILLING_DATE_PICKER_FORMAT}
                acceptTypedDates
                styleType={"ghost"}
                fontSize={"13.5px"}
                width={"110px"}
                onChange={(date) => setPaymentDate(transformDateToUTC(date))}
              />
            </div>
            <LuCalendar size={15} className={"shrink-0 text-[#9AA0B0]"} />
          </div>
          {showDateError && dateError && (
            <div
              role={"alert"}
              className={"max-w-[260px] text-[12px] font-medium text-[#B42318]"}
            >
              {dateError}
            </div>
          )}
        </div>
        <label className={"flex flex-col gap-[7px]"}>
          <span className={"text-xs font-bold text-[#9097A8]"}>
            Payment method
          </span>
          <div className={"relative w-[190px]"}>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className={
                "h-[42px] w-full cursor-pointer appearance-none rounded-[9px] border border-[#DFE2E8] bg-white pl-3 pr-8 text-[13.5px] text-[#1F2433] outline-none focus:border-[#6B4EFF]"
              }
            >
              {paymentMethods?.map((item) => (
                <option value={item?.id} key={item?.id}>
                  {item?.paymentMethod}
                </option>
              ))}
            </select>
            <LuChevronDown
              size={13}
              className={
                "pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#9AA0B0]"
              }
            />
          </div>
        </label>
      </div>

      {currentContact && (
        <div
          className={"mt-5 overflow-hidden rounded-xl border border-[#EEF0F3]"}
        >
          <div
            className={
              "grid grid-cols-[44px_1fr_1fr_1fr_1fr_1.4fr] gap-2.5 border-b border-[#EEF0F3] bg-[#FAFBFC] px-[18px] py-3 text-[12.5px] font-bold text-[#8B92A2]"
            }
          >
            <span />
            <span>Invoice #</span>
            <span>Due date</span>
            <span>Original amount</span>
            <span>
              {/* Edit: what the invoice owes without this payment (PAY-018). */}
              {isEdit ? "Balance before payment" : "Balance"}
            </span>
            <span className={"text-right"}>Amount paid</span>
          </div>
          {isLoading || (isEdit && !invoices) ? (
            <LottieLoading />
          ) : data.length ? (
            data.map((item, index) => (
              <div
                key={item.id || index}
                className={
                  "grid grid-cols-[44px_1fr_1fr_1fr_1fr_1.4fr] items-center gap-2.5 border-b border-[#F4F5F7] px-[18px] py-[13px] text-[13.5px] text-[#3A4256] last:border-b-0"
                }
              >
                <BillingCheckbox
                  checked={item.isChecked}
                  label={`Pay invoice ${item.invoiceId}`}
                  onChange={(checked) =>
                    updateRow(index, {
                      isChecked: checked,
                      amountPaid: checked
                        ? item?.balance?.toString() || ""
                        : "",
                    })
                  }
                />
                <span className={"font-semibold text-[#1F2433]"}>
                  {item?.invoiceId}
                </span>
                <span>{formatBillingDate(item?.dueDate)}</span>
                <span className={"tabular-nums"}>
                  {formatMoney(item?.originalAmount)}
                </span>
                <span className={"tabular-nums"}>
                  {formatMoney(item.balance)}
                </span>
                <CustomCurrencyInput
                  allowNegative={false}
                  value={item?.amountPaid}
                  className={
                    "!h-[38px] !rounded-lg !border-[#DFE2E8] !px-3 !text-right !text-[13px] tabular-nums focus:!border-[#6B4EFF] focus:!outline-none"
                  }
                  setValue={(value) => {
                    setAmountProblem(null);
                    updateRow(index, { amountPaid: value || "" });
                  }}
                />
              </div>
            ))
          ) : (
            <div
              className={
                "px-[18px] py-6 text-center text-[13px] text-[#8B92A2]"
              }
            >
              This client has no unpaid invoices.
            </div>
          )}
        </div>
      )}

      <div className={"mt-[18px] flex items-center justify-end"}>
        <div
          className={
            "flex items-center gap-4 rounded-[10px] border border-[#E6E0FF] bg-[#F7F5FF] px-[18px] py-3"
          }
        >
          <span className={"text-[13px] font-bold text-[#3A4256]"}>
            Amount paid
          </span>
          <span
            className={"text-lg font-extrabold tabular-nums text-[#6B4EFF]"}
          >
            {formatMoney(total)}
          </span>
        </div>
      </div>

      <label className={"mt-[22px] block"}>
        <span className={"mb-2 block text-[12.5px] font-bold text-[#3A4256]"}>
          Notes
        </span>
        <textarea
          className={
            "block min-h-[90px] w-full max-w-[340px] resize-y rounded-[10px] border border-[#EAECEF] px-3.5 py-3 text-[13px] text-[#1F2433] outline-none placeholder:text-[#9AA0B0] focus:border-[#6B4EFF]"
          }
          placeholder={"Add a note (optional)..."}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </label>

      {errorMessage && (
        <div
          role={"alert"}
          className={
            "mt-4 rounded-lg border border-[#F5D0D0] bg-[#FDF3F3] px-3 py-2 text-[13px] text-[#B42318]"
          }
        >
          {errorMessage}
        </div>
      )}

      <div className={"mt-6 flex items-center justify-end gap-3.5"}>
        <button
          type={"button"}
          onClick={() => navigate(-1)}
          className={classNames(BILLING_SECONDARY_BUTTON, "h-[42px] px-[22px]")}
        >
          Cancel
        </button>
        <button
          type={"button"}
          onClick={() => createPayment()}
          disabled={!currentContact || isSaving}
          className={classNames(
            BILLING_PRIMARY_BUTTON,
            "h-[42px] rounded-[9px] px-[30px]",
          )}
        >
          Save
        </button>
      </div>

      {overpayQuestion &&
        createPortal(
          <SettingsConfirmModal
            onClose={() => setOverpayQuestion(null)}
            onConfirm={() => {
              setOverpayQuestion(null);
              createPayment(true);
            }}
            icon={<LuAlertCircle size={24} />}
            variant={"warning"}
            title={"More than the balance"}
            description={escapeHtml(overpayQuestion)}
            confirmText={isEdit ? "Save anyway" : "Record payment"}
          />,
          document.body,
        )}
    </div>
  );
};

/** Single client dropdown with a search box (V49 design). */
const PaymentClientSelect: React.FC<{
  value: SelectOptionType | null;
  options: SelectOptionType[];
  search: string;
  isLoading?: boolean;
  /** Editing a saved payment: the client can't be changed (owner 2026-10-23). */
  locked?: boolean;
  onSearch: (value: string) => void;
  onChange: (option: SelectOptionType) => void;
}> = ({ value, options, search, isLoading, locked, onSearch, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [isOpen]);

  const toClient = (option: SelectOptionType) => ({
    id: String(option.value),
    name: String(option.label || ""),
    isOrganization: option.avatarColor === "purple",
  });

  return (
    <div ref={ref} className={"relative max-w-[340px]"}>
      <button
        type={"button"}
        aria-label={"Client"}
        aria-expanded={isOpen}
        disabled={locked}
        title={locked ? "The client of a saved payment can't be changed." : undefined}
        onClick={() => !locked && setIsOpen((open) => !open)}
        className={classNames(
          "flex h-[42px] w-full items-center gap-[9px] rounded-[9px] border-[1.5px] px-3 text-left",
          locked
            ? "cursor-not-allowed border-[#DFE2E8] bg-[#F6F7F9]"
            : isOpen || value
              ? "border-[#6B4EFF] bg-white"
              : "border-[#DFE2E8] bg-white",
        )}
      >
        {value && <ClientAvatar client={toClient(value)} size={24} />}
        <span
          className={classNames(
            "flex-1 truncate text-[13.5px]",
            value ? "text-[#1F2433]" : "text-[#9AA0B0]",
          )}
        >
          {value ? value.label : "Search client"}
        </span>
        {!locked && <LuChevronDown size={14} className={"text-[#9AA0B0]"} />}
      </button>
      {isOpen && (
        <div
          className={
            "absolute left-0 right-0 top-[calc(100%+6px)] z-[21] max-h-[280px] overflow-y-auto rounded-[10px] border border-[#E8E8EC] bg-white p-2 shadow-[0_14px_36px_rgba(20,22,34,0.18)]"
          }
        >
          <input
            autoFocus
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            placeholder={"Search clients..."}
            className={
              "mb-1.5 h-[34px] w-full rounded-lg border border-[#E4E6EB] px-3 text-[13px] text-[#1F2433] outline-none placeholder:text-[#9AA0B0] focus:border-[#6B4EFF]"
            }
          />
          {options.map((option) => (
            <button
              type={"button"}
              key={String(option.value)}
              onClick={() => {
                onChange(option);
                setIsOpen(false);
              }}
              className={
                "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-[#F4F2FF]"
              }
            >
              <ClientAvatar client={toClient(option)} />
              <span className={"text-[13.5px] font-medium text-[#1F2433]"}>
                {option.label}
              </span>
            </button>
          ))}
          {isLoading && (
            <div className={"px-2.5 py-2 text-[13px] text-[#9097A8]"}>
              Loading...
            </div>
          )}
          {!isLoading && !options.length && (
            <div className={"px-2.5 py-2 text-[13px] text-[#9097A8]"}>
              No clients found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AddPayment;
